import { authService } from '../../services/auth';
import { ApiError, authorizedFetch } from '../../services/core.api';
import { damageReportsApi, type DamageReportDto } from '../../services/damage-reports.api';
import { i18nService, onLocaleChange, t } from '../../services/i18n';
import {
  getTenantPortalSectionItems,
  tenantPortalApi,
  type TenantPortalOverviewDto,
  type TenantPortalPropertyDto,
  type TenantPortalSection,
} from '../../services/tenant-portal.api';

type TenantPortalNavItem = {
  section: TenantPortalSection;
  href: string;
  titleKey: string;
};

const SECTION_NAV: TenantPortalNavItem[] = [
  { section: 'active', href: '/tenant/active/', titleKey: 'tenantPortal.activeTitle' },
  { section: 'upcoming', href: '/tenant/upcoming/', titleKey: 'tenantPortal.futureTitle' },
  { section: 'history', href: '/tenant/history/', titleKey: 'tenantPortal.pastTitle' },
  { section: 'applications', href: '/tenant/applications/', titleKey: 'tenantPortal.candidateTitle' },
];

const SECTION_META: Record<TenantPortalSection, { titleKey: string; descriptionKey: string; accentClass: string }> = {
  active: {
    titleKey: 'tenantPortal.activeTitle',
    descriptionKey: 'tenantPortal.activeDescription',
    accentClass: 'tenant-portal-hero--active',
  },
  upcoming: {
    titleKey: 'tenantPortal.futureTitle',
    descriptionKey: 'tenantPortal.futureDescription',
    accentClass: 'tenant-portal-hero--upcoming',
  },
  applications: {
    titleKey: 'tenantPortal.candidateTitle',
    descriptionKey: 'tenantPortal.candidateDescription',
    accentClass: 'tenant-portal-hero--applications',
  },
  history: {
    titleKey: 'tenantPortal.pastTitle',
    descriptionKey: 'tenantPortal.pastDescription',
    accentClass: 'tenant-portal-hero--history',
  },
};

export class RcTenantPortalPage extends HTMLElement {
  private section: TenantPortalSection = 'active';
  private overview: TenantPortalOverviewDto | null = null;
  private isLoading = false;
  private error = '';
  private disposeLocaleChange?: () => void;
  private submittingAgreementIds = new Set<string>();
  private reportErrors = new Map<string, string>();

  private boundAuthChange = (authenticated: boolean) => {
    if (!authenticated) {
      this.overview = null;
      this.error = '';
      this.isLoading = false;
      this.render();
      return;
    }

    void this.load();
  };

  connectedCallback(): void {
    this.section = this.resolveSection(this.getAttribute('section'));
    authService.onAuthChange(this.boundAuthChange);
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
    this.render();

    if (authService.isAuthenticated) {
      void this.load();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
  }

  private resolveSection(value: string | null): TenantPortalSection {
    return value === 'applications' || value === 'active' || value === 'upcoming' || value === 'history'
      ? value
      : 'active';
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();

    try {
      this.overview = await tenantPortalApi.getOverview();
    } catch (error: any) {
      this.error = error?.message || t('tenantPortal.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  render(): void {
    const meta = SECTION_META[this.section];
    const sectionItems = this.overview ? getTenantPortalSectionItems(this.overview, this.section) : [];
    const counts = this.getCounts();
    const currentNavItem = SECTION_NAV.find((item) => item.section === this.section) ?? SECTION_NAV[0];

    this.innerHTML = `
      <div class="tenant-portal-shell">
        <section class="tenant-portal-hero ${meta.accentClass}">
          <div class="tenant-portal-hero-copy">
            <span class="tenant-portal-eyebrow">${t('tenantPortal.eyebrow')}</span>
            <h1>${t(meta.titleKey)}</h1>
            <p>${t(meta.descriptionKey)}</p>
          </div>
          <div class="tenant-portal-hero-stat">
            <span class="tenant-portal-hero-stat-label">${t('tenantPortal.currentSectionCount')}</span>
            <strong>${sectionItems.length}</strong>
            <span>${t(currentNavItem.titleKey)}</span>
          </div>
        </section>

        <nav class="tenant-portal-switcher" aria-label="${t('tenantPortal.navigationLabel')}">
          ${SECTION_NAV.map((item) => `
            <a href="${item.href}" class="tenant-portal-switcher-item${item.section === this.section ? ' active' : ''}">
              <span>${t(item.titleKey)}</span>
              <strong>${counts[item.section]}</strong>
            </a>
          `).join('')}
        </nav>

        ${this.renderContent(sectionItems)}
      </div>
    `;
  }

  private renderContent(sectionItems: TenantPortalPropertyDto[]): string {
    if (this.isLoading) {
      return `<div class="card tenant-portal-panel"><div class="empty-state">${t('tenantPortal.loading')}</div></div>`;
    }

    if (this.error) {
      return `<div class="card tenant-portal-panel"><div class="form-error">${this.escape(this.error)}</div></div>`;
    }

    if (!sectionItems.length) {
      return `
        <div class="card tenant-portal-panel tenant-portal-empty">
          <div class="tenant-portal-empty-visual"></div>
          <div>
            <h2>${t('tenantPortal.emptyTitle')}</h2>
            <p>${t('tenantPortal.emptyBody')}</p>
          </div>
        </div>
      `;
    }

    return `
      <section class="tenant-portal-card-grid">
        ${sectionItems.map((item) => this.renderCard(item)).join('')}
      </section>
    `;
  }

  private renderCard(item: TenantPortalPropertyDto): string {
    const isCandidate = this.section === 'applications';
    const isActive = this.section === 'active';
    const dateRows = isCandidate
      ? this.renderMetaRow(t('tenantPortal.interestDate'), this.formatDate(item.interestDate))
      : [
          this.renderMetaRow(t('tenantPortal.startDate'), this.formatDate(item.startDate)),
          this.renderMetaRow(t('tenantPortal.endDate'), this.formatDate(item.endDate)),
          this.renderMetaRow(t('tenantPortal.rentAmount'), this.formatMoney(item.rentAmount)),
        ].join('');

    return `
      <article class="card tenant-portal-card">
        <div class="tenant-portal-card-head">
          <div>
            <span class="tenant-portal-card-kicker">${this.escape(item.landlordDisplayName || '-')}</span>
            <h2>${this.escape(item.propertyAddress)}</h2>
          </div>
          <span class="tenant-portal-card-badge">${this.escape(item.agreementStatus || item.propertyStatus || '-')}</span>
        </div>
        <p class="tenant-portal-card-status">${t('tenantPortal.propertyStatus')}: ${this.escape(item.propertyStatus || '-')}</p>
        <div class="tenant-portal-card-meta">
          ${dateRows}
        </div>
        ${this.renderDamageReports(item)}
        ${item.welcomePageUrl ? `
          <div class="tenant-portal-card-actions">
            <a
              class="button button-secondary"
              href="${this.escape(item.welcomePageUrl)}"
              target="_blank"
              rel="noopener noreferrer"
            >${t('tenantPortal.viewWelcomePage')}</a>
          </div>
        ` : ''}
      </article>
    `;
  }

  private renderDamageReports(item: TenantPortalPropertyDto): string {
    const reports = item.damageReports || [];
    const isActive = this.section === 'active';
    const error = this.reportErrors.get(item.rentalAgreementId) || '';
    const busy = this.submittingAgreementIds.has(item.rentalAgreementId);

    if (!isActive && reports.length === 0) {
      return '';
    }

    return `
      <section class="tenant-portal-report-block" style="margin-top:1rem;padding-top:1rem;border-top:1px solid rgba(0,0,0,0.08);">
        <div class="tenant-portal-report-header" style="display:flex;justify-content:space-between;gap:1rem;align-items:flex-start;">
          <div>
            <strong>${t('tenantPortal.damageReports.title')}</strong>
            <div class="text-muted text-sm">${t('tenantPortal.damageReports.helper')}</div>
          </div>
          <span class="badge badge-info">${reports.length}</span>
        </div>
        ${reports.length === 0 ? `<p class="text-muted text-sm" style="margin-top:0.75rem;">${t('tenantPortal.damageReports.empty')}</p>` : `
          <div style="display:flex;flex-direction:column;gap:0.75rem;margin-top:0.75rem;">
            ${reports.map((report) => this.renderDamageReport(report)).join('')}
          </div>
        `}
        ${isActive ? `
          <form data-rental-agreement-id="${this.escape(item.rentalAgreementId)}" style="margin-top:1rem;display:flex;flex-direction:column;gap:0.75rem;">
            <label style="display:flex;flex-direction:column;gap:0.35rem;">
              <span>${t('tenantPortal.damageReports.descriptionLabel')}</span>
              <textarea name="description" rows="4" required maxlength="4000" placeholder="${this.escape(t('tenantPortal.damageReports.descriptionPlaceholder'))}"></textarea>
            </label>
            <label style="display:flex;flex-direction:column;gap:0.35rem;">
              <span>${t('tenantPortal.damageReports.photosLabel')}</span>
              <input type="file" name="photos" accept="image/*" multiple>
            </label>
            ${error ? `<div class="form-error">${this.escape(error)}</div>` : ''}
            <div class="tenant-portal-card-actions">
              <button class="button button-secondary" type="submit" ${busy ? 'disabled' : ''}>${busy ? t('common.uploading') : t('tenantPortal.damageReports.submit')}</button>
            </div>
          </form>
        ` : ''}
      </section>
    `;
  }

  private renderDamageReport(report: DamageReportDto): string {
    return `
      <article class="card" style="padding:0.9rem;">
        <div class="tenant-portal-meta-row">
          <span>${this.escape(report.tenantName || '')}</span>
          <strong>${this.escape(i18nService.formatDate(report.createdAt, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}</strong>
        </div>
        <p style="margin:0.5rem 0 0;">${this.escape(report.description)}</p>
        ${report.photos.length > 0 ? `
          <div style="display:flex;gap:0.5rem;flex-wrap:wrap;margin-top:0.75rem;">
            ${report.photos.map((photo) => `
              <button type="button" class="button button-secondary" data-photo-url="${this.escape(photo.downloadUrl)}">${this.escape(photo.fileName)}</button>
            `).join('')}
          </div>
        ` : ''}
      </article>
    `;
  }

  private renderMetaRow(label: string, value: string): string {
    return `
      <div class="tenant-portal-meta-row">
        <span>${this.escape(label)}</span>
        <strong>${this.escape(value)}</strong>
      </div>
    `;
  }

  private getCounts(): Record<TenantPortalSection, number> {
    return {
      active: this.overview?.activeProperties.length ?? 0,
      upcoming: this.overview?.futureProperties.length ?? 0,
      applications: this.overview?.candidateProperties.length ?? 0,
      history: this.overview?.pastProperties.length ?? 0,
    };
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return t('tenantPortal.notAvailable');
    }

    return i18nService.formatDate(value, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private formatMoney(value?: number | null): string {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return t('tenantPortal.notAvailable');
    }

    return i18nService.formatCurrency(value);
  }

  private escape(value: string): string {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  private handleSubmit = async (event: Event): Promise<void> => {
    if (!(event instanceof SubmitEvent)) {
      return;
    }

    const form = event.target as HTMLFormElement | null;
    if (!form || !form.dataset.rentalAgreementId) {
      return;
    }

    event.preventDefault();
    const rentalAgreementId = form.dataset.rentalAgreementId;
    const description = (new FormData(form).get('description') || '').toString().trim();
    const fileInput = form.querySelector<HTMLInputElement>('input[type="file"][name="photos"]');
    const files = Array.from(fileInput?.files || []);

    this.submittingAgreementIds.add(rentalAgreementId);
    this.reportErrors.delete(rentalAgreementId);
    this.render();

    try {
      const report = await damageReportsApi.createForTenantAgreement(rentalAgreementId, { description });
      for (const file of files) {
        const upload = await damageReportsApi.createPhotoUpload(report.id, {
          fileName: file.name,
          contentType: file.type || 'image/jpeg',
          fileSizeBytes: file.size,
        });

        const response = await fetch(upload.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'image/jpeg' },
          body: file,
        });

        if (!response.ok) {
          throw new Error(t('document.uploadError'));
        }
      }

      form.reset();
      await this.load();
    } catch (error: any) {
      this.reportErrors.set(
        rentalAgreementId,
        error instanceof ApiError ? error.message : (error?.message || t('tenantPortal.damageReports.submitError')),
      );
    } finally {
      this.submittingAgreementIds.delete(rentalAgreementId);
      this.render();
    }
  };

  private handleClick = async (event: Event): Promise<void> => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('[data-photo-url]');
    if (!button?.dataset.photoUrl) {
      return;
    }

    const response = await authorizedFetch(button.dataset.photoUrl);
    if (!response.ok) {
      return;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
}

if (!customElements.get('rc-tenant-portal-page')) {
  customElements.define('rc-tenant-portal-page', RcTenantPortalPage);
}
